// Keep this here. Needs to be on the first .ts page that's hit.
import { computed, defineComponent, onBeforeUnmount, onMounted, onUpdated } from 'vue'

import { useMeta as useDocumentHead } from 'quasar'

import { useVm } from 'booksprout-app'
import { useAppRuntime } from 'booksprout-app/src/composables/useAppRuntime'
import { useMeta } from 'booksprout-app/src/composables/useMeta'
import { useScreen } from 'booksprout-app/src/composables/useScreen'
import { useUserHelpers } from 'booksprout-app/src/composables/useUserHelpers'
import { useUi } from 'booksprout-app/src/modules/ui/uiModule'

require('promise.prototype.finally').shim()

/**
 * The website's root component. `BaseApp`'s lifecycle is `useAppRuntime` (stage 8), so what is left is the
 * hook wiring every app repeats and this app's one addition, the router-view key.
 *
 * The website has no ui-store watchers of its own: it never redirects, never opens the shared message
 * dialog and has no auth-expiry prompt, because a visitor here is not signed in.
 *
 * It also never polls. The per-minute routines are author/reviewer app work — unread message counts, user
 * stats, HelpHero — and they reach for a notification and a message-thread service the website has no use
 * for. They were inert only because no user was ever loaded here; now that the auth boot loads one, they
 * are left unstarted. The website wants the identity, not the polling [sc-27755].
 *
 * On dev this is a `runIntervalActions = false` field on `BaseApp` that makes `intervalRoutines` return
 * early, with the interval still firing every minute to do nothing. Here the caller owns that decision, so
 * not starting it is the same thing said once.
 */
export default defineComponent({
  name: 'AppComponent',

  /**
   * The document head, published from the ROOT component -- which is the only component a server render
   * reaches. `<router-view v-if="isReadyToRender">` is false until `mounted`, which does not run on the
   * server, so the layout that also carries `meta ()` is never created there.
   *
   * This is where it lived before stage 8, by inheritance: `BaseApp extends CommonBaseMixin`, whose
   * `meta ()` Quasar collects off the root component. Converting App.ts onto `useAppRuntime` dropped
   * that, and with it every server-rendered title, description, og tag and canonical link -- verified
   * against the deployed environment, which serves no `<title>` at all where production serves a full
   * head (sc-27856).
   *
   * The layout keeps its own: Quasar merges the two, so its title template still wins on the client.
   */
  meta () {
    return useMeta().documentMeta()
  },

  setup () {
    /**
     * The document head, published from the ROOT component -- which is the only place a server render
     * reaches. `<router-view v-if="isReadyToRender">` below is false until `onMounted`, which never runs
     * on the server, so the layout that also publishes the head is never created there: the head this
     * app serves to crawlers comes from here and nowhere else.
     *
     * This is where Vue 2 had it too, by inheritance -- `BaseApp extends CommonBaseMixin`, whose
     * `meta ()` option Quasar 1 collected off the root component. Converting App.ts off BaseApp dropped
     * it, and with it every server-rendered title, description, og tag and canonical link (sc-27856).
     * The layouts keep their own call: the reviewer app's overrides the title template, and Quasar
     * merges the list in push order, so the layout's wins on the client exactly as it did before.
     */
    useDocumentHead(() => useMeta().documentMeta())

    const vm = useVm()
    const userHelpers = useUserHelpers()

    const runtime = useAppRuntime({
      host: vm as never,
      mimicOrAuthenticatedUser: () => userHelpers.mimicOrAuthenticatedUser,
      authenticatedUser: () => userHelpers.authenticatedUser,
      isMobileApp: () => useScreen(vm as never).isMobileApp(),
      isReviewerApp: () => false,
    })

    const isReadyToRender = computed(() => useUi().ui.store.getters.isReadyToRender as boolean)

    /** Keyed on the id route param so moving between two of the same page remounts it. */
    const routerViewKey = computed(() => vm.$route.params.id || '')

    /**
     * `BaseApp.created` installed the icon resolver, which has to happen before the first render — so it
     * stays in the setup body rather than moving to `onMounted`.
     */
    runtime.configureIcons()

    onMounted(() => {
      runtime.configureLoading()
      runtime.configureCalendar()
      runtime.startHelpHero(runtime.onChecklistCompleted)

      void useUi().ui.setReadyToRender(true)
    })

    onUpdated(() => {
      runtime.captureReferral()
    })

    onBeforeUnmount(() => {
      runtime.stopHelpHero(runtime.onChecklistCompleted)
    })

    return {
      isReadyToRender,
      routerViewKey,
      onChecklistCompleted: runtime.onChecklistCompleted
    }
  }
})
